import React from 'react';

const SkeletonLoader = ({ 
  className = "", 
  variant = "default",
  width = "w-full",
  height = "h-4",
  rounded = "rounded"
}) => {
  const baseClasses = "animate-pulse bg-gray-200 dark:bg-gray-700";
  const combinedClasses = `${baseClasses} ${width} ${height} ${rounded} ${className}`;

  return <div className={combinedClasses} />;
};

// Enhanced Course Card Skeleton
const CourseCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
    <div className="bg-gray-300 h-48 w-full" />
    <div className="p-6">
      <div className="h-6 bg-gray-300 rounded w-3/4 mb-3" />
      <div className="h-4 bg-gray-300 rounded w-full mb-2" />
      <div className="h-4 bg-gray-300 rounded w-2/3 mb-4" />
      <div className="flex justify-between items-center">
        <div className="h-8 bg-gray-300 rounded w-20" />
        <div className="h-10 bg-gray-300 rounded w-24" />
      </div>
    </div>
  </div>
);

// Enhanced Table Skeleton
const TableSkeleton = ({ rows = 5, columns = 4 }) => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
    <div className="bg-gray-100 p-4 border-b">
      <div className="h-6 bg-gray-300 rounded w-1/3" />
    </div>
    <div className="divide-y divide-gray-200">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="p-4">
          <div className="grid grid-cols-4 gap-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <div key={colIndex} className="h-4 bg-gray-300 rounded" />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Dashboard Stats Skeleton
const DashboardStatsSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-8 w-8 bg-gray-300 rounded" />
          <div className="h-4 w-16 bg-gray-300 rounded" />
        </div>
        <div className="h-8 bg-gray-300 rounded w-20 mb-2" />
        <div className="h-3 bg-gray-300 rounded w-24" />
      </div>
    ))}
  </div>
);

// Form Skeleton
const FormSkeleton = ({ fields = 4 }) => (
  <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
    {Array.from({ length: fields }).map((_, index) => (
      <div key={index} className="mb-6">
        <div className="h-4 bg-gray-300 rounded w-1/4 mb-2" />
        <div className="h-10 bg-gray-300 rounded w-full" />
      </div>
    ))}
    <div className="flex gap-4">
      <div className="h-10 bg-gray-300 rounded w-24" />
      <div className="h-10 bg-gray-300 rounded w-16" />
    </div>
  </div>
);

// User Card Skeleton
const UserCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
    <div className="flex items-center space-x-4">
      <div className="h-16 w-16 bg-gray-300 rounded-full" />
      <div className="flex-1">
        <div className="h-5 bg-gray-300 rounded w-1/3 mb-2" />
        <div className="h-4 bg-gray-300 rounded w-1/2 mb-1" />
        <div className="h-3 bg-gray-300 rounded w-2/3" />
      </div>
      <div className="flex space-x-2">
        <div className="h-8 w-8 bg-gray-300 rounded" />
        <div className="h-8 w-8 bg-gray-300 rounded" />
      </div>
    </div>
  </div>
);

// Loading Spinner with text
const LoadingSpinner = ({ text = "Loading...", size = "md" }) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6", 
    lg: "w-8 h-8",
    xl: "w-12 h-12"
  };

  return (
    <div className="flex items-center justify-center space-x-2">
      <div 
        className={`animate-spin rounded-full border-2 border-gray-300 border-t-safir-primary ${sizeClasses[size]}`}
      />
      {text && <span className="text-gray-600">{text}</span>}
    </div>
  );
};

// Page Loader
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <LoadingSpinner size="xl" text="Loading Safir Elite..." />
    </div>
  </div>
);

export { 
  SkeletonLoader, 
  CourseCardSkeleton, 
  TableSkeleton, 
  DashboardStatsSkeleton,
  FormSkeleton,
  UserCardSkeleton,
  LoadingSpinner,
  PageLoader
};
